import React, { useCallback, useState } from 'react';
import { UploadCloud, Loader2, FileText, Images, Download } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Accepts a single CSV/PDF or multiple screenshot images (multi-file).
// Any drop containing images auto-routes to the multi-screenshot endpoint.
const InstitutionImportDropZone = ({ files, file, stage, onFileSelected, fileInputRef }) => {
  const [dragOver, setDragOver] = useState(false);
  const busy = stage === 'reading';
  const staged = files && files.length > 0 ? files : (file ? [file] : []);

  const handleDrop = useCallback((e) => {
    e.preventDefault(); setDragOver(false);
    const dropped = e.dataTransfer?.files;
    if (dropped && dropped.length) onFileSelected(dropped);
  }, [onFileSelected]);

  const handleChange = useCallback((e) => {
    if (e.target.files?.length) onFileSelected(e.target.files);
  }, [onFileSelected]);

  const renderStaged = () => {
    if (staged.length > 1) {
      const bytes = staged.reduce((n, f) => n + (f.size || 0), 0);
      return (
        <div className="flex flex-col items-center gap-2 text-slate-700">
          <Images className="w-10 h-10 text-emerald-600" />
          <p className="font-medium text-slate-900">
            {staged.length} screenshots ready
          </p>
          <p className="text-xs text-slate-500">Total {(bytes / 1024).toFixed(1)} KB</p>
          <p className="text-xs text-emerald-700 mt-1">Click to change your selection</p>
        </div>
      );
    }
    const single = staged[0];
    return (
      <div className="flex flex-col items-center gap-2 text-slate-700">
        <FileText className="w-10 h-10 text-emerald-600" />
        <p className="font-medium text-slate-900">{single.name}</p>
        <p className="text-xs text-slate-500">{(single.size / 1024).toFixed(1)} KB</p>
        <p className="text-xs text-emerald-700 mt-1">Click to choose a different file</p>
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid="institution-import-drop-zone-wrapper">
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => !busy && fileInputRef.current?.click()}
        className={`cursor-pointer border-2 border-dashed rounded-xl p-10 text-center transition-colors ${
          busy ? 'border-slate-200 bg-slate-50 cursor-wait'
               : dragOver ? 'border-emerald-400 bg-emerald-50'
                          : 'border-slate-300 hover:border-emerald-300 hover:bg-slate-50'
        }`}
        data-testid="institution-import-drop-zone"
      >
        <input
          ref={fileInputRef} type="file" className="hidden" multiple
          accept=".csv,.txt,.xls,.xlsx,.pdf,.png,.jpg,.jpeg,.webp"
          onChange={handleChange} data-testid="institution-import-file-input"
        />
        {busy ? (
          <div className="flex flex-col items-center gap-3 text-slate-600">
            <Loader2 className="w-10 h-10 animate-spin text-emerald-600" />
            <p className="text-sm">
              {staged.length > 1 ? `Reading ${staged.length} screenshots…` : 'Reading your file…'}
            </p>
          </div>
        ) : staged.length > 0 ? renderStaged() : (
          <div className="flex flex-col items-center gap-3 text-slate-600">
            <UploadCloud className="w-10 h-10 text-slate-400" />
            <div>
              <p className="font-medium text-slate-800">Drop your institution&rsquo;s file here</p>
              <p className="text-sm text-slate-500 mt-1">
                One CSV or PDF &mdash; or up to 6 screenshots from your mobile portal
              </p>
            </div>
          </div>
        )}
      </div>
      <p className="text-xs text-slate-500 text-center">
        We never guess at numbers. If we can&rsquo;t read a file with full confidence, we&rsquo;ll tell you honestly.
      </p>
      {staged.length === 0 && !busy && (
        <p className="text-xs text-slate-500 text-center">
          Don&rsquo;t have a file yet?{' '}
          <a
            href={`${BACKEND_URL}/api/investments/templates/quoted_stocks`}
            download
            className="text-emerald-700 hover:text-emerald-800 underline underline-offset-2 inline-flex items-center gap-1"
            data-testid="institution-import-template-download"
          >
            <Download className="w-3 h-3" /> Download our MyInvestFolio template CSV
          </a>
        </p>
      )}
    </div>
  );
};

export default InstitutionImportDropZone;
